import { MenuLoading } from '@/catalog-apps/components/AppMenu/AppMenu'
import { MAIN_CONTENT_HEIGHT, SIDEBAR_WIDTH } from '@/catalog-apps/components/config'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { useSharedSidebarState } from '@/hooks'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { Sidebar, SidebarContent, SidebarProvider } from '@/components/shadcn/sidebar'
import { SidebarToggleButton } from '@/components/ui/SidebarToggleButton'
import { Suspense, cloneElement, isValidElement, type CSSProperties } from 'react'
import { Outlet } from 'react-router'
import { SavedPageLayouts } from '@/pages/Applications/AppPageBuilderPage/SavedPageLayouts'

export const ManagementPageLayout = ({
  menu,
  secondaryMenu,
  content,
}: {
  menu: React.ReactNode
  secondaryMenu?: React.ReactNode
  content?: React.ReactNode
}) => {
  const isPageBuilder = useIsPageBuilder()
  const [sidebarOpen, setSidebarOpen] = useSharedSidebarState(true)
  const isSidebarForcedOpen = Boolean(secondaryMenu) || isPageBuilder
  const effectiveSidebarOpen = isSidebarForcedOpen || sidebarOpen
  const resolvedMenu = isValidElement<{ sidebarOpen?: boolean }>(menu)
    ? cloneElement(menu, { sidebarOpen: effectiveSidebarOpen })
    : menu
  const resolvedSecondaryMenu = isValidElement<{ sidebarOpen?: boolean }>(secondaryMenu)
    ? cloneElement(secondaryMenu, { sidebarOpen: effectiveSidebarOpen })
    : secondaryMenu

  return (
    <SidebarProvider
      open={effectiveSidebarOpen}
      onOpenChange={(open) => {
        if (!isSidebarForcedOpen) {
          setSidebarOpen(open)
        }
      }}
      style={
        {
          '--sidebar-width': `${SIDEBAR_WIDTH}px`,
          '--sidebar-width-icon': '55px',
          height: MAIN_CONTENT_HEIGHT,
          gridTemplateColumns: 'auto minmax(0, 1fr)',
        } as CSSProperties
      }
      className="grid w-full min-w-0"
    >
      <Sidebar
        style={{ height: MAIN_CONTENT_HEIGHT }}
        className="relative border-r bg-offwhite"
        collapsedClassName="w-[65px]"
      >
        <div className="flex h-full min-h-0 flex-col">
          <SidebarContent className="min-h-0 flex-1">
            <ScrollArea className="h-full pl-2" scrollBarClassName="w-2">
              <Suspense fallback={<MenuLoading />}>
                <div className="pb-2">{resolvedMenu}</div>
              </Suspense>
            </ScrollArea>
          </SidebarContent>
          {resolvedSecondaryMenu && <div className="h-min w-full shrink-0 pb-2 pl-3 pr-2">{resolvedSecondaryMenu}</div>}
          {isPageBuilder && (
            <div className="h-1/2 w-full shrink-0 rounded-tr-3xl pr-2">
              <div className="h-full w-full overflow-hidden rounded-tr-3xl bg-foreground/5">
                <SavedPageLayouts />
              </div>
            </div>
          )}
          <div className="flex shrink-0 justify-end border-t border-border/40 bg-offwhite px-1 py-2">
            <SidebarToggleButton
              variant="footer"
              onToggle={() => setSidebarOpen((curr) => !curr)}
              open={effectiveSidebarOpen}
              disabled={isSidebarForcedOpen}
            />
          </div>
        </div>
      </Sidebar>
      {isPageBuilder ? (
        (content ?? <Outlet />)
      ) : (
        <ScrollArea
          className="relative min-w-0"
          style={{ height: MAIN_CONTENT_HEIGHT }}
          viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
        >
          <div style={{ minHeight: MAIN_CONTENT_HEIGHT }} className="flex w-full min-w-0 flex-col px-5">
            {content ?? <Outlet />}
          </div>
        </ScrollArea>
      )}
    </SidebarProvider>
  )
}
